// src/services/EncryptionService.ts

import CryptoService from './CryptoService';
import CryptoUtilities from './CryptoUtilities';

/**
 * Classe per gestire la crittografia e la decrittografia delle chiavi.
 */
export class EncryptionService {
  private cryptoService: CryptoService;

  constructor() {
    this.cryptoService = CryptoService.getInstance();
  }

  /**
   * Crittografa la chiave principale utilizzando un nome utente e una passphrase.
   * @param username - Il nome utente dell'utente.
   * @param passphrase - La passphrase dell'utente.
   * @param masterKey - La chiave principale da crittografare.
   * @param iterations - Il numero di iterazioni per PBKDF2.
   * @returns La chiave principale crittografata in base64.
   */
  public async encryptMasterKey(
    username: string,
    passphrase: string,
    masterKey: ArrayBuffer,
    iterations: number = 1000
  ): Promise<string> {
    // FASE-2: Generazione enc_key
    const salt = await this.cryptoService.hashSHA256(username);
    const encKey = await this.cryptoService.pbkdf2(
      passphrase,
      salt,
      iterations
    );
    // FASE-3: Generazione enc_mk con IV (IV usato internamente in encryptCBC)
    const iv = await this.cryptoService.generateIV();
    const encMK = await this.cryptoService.encryptCBC(encKey, masterKey, iv);

    // FASE-4 & 5: Generazione mac_key e mac (32 bytes)

    const macKey = await this.deriveMacKey(username, passphrase);
    const mac = await this.cryptoService.calculateMac(macKey, encMK);

    // FASE-6: Concatenazione finale (MAC[32] + ENC_MK[64] = 96 bytes -> 128 caratteri base64)
    const result = CryptoUtilities.arrayBufferToBase64(
      CryptoUtilities.concatArrayBuffer(mac, encMK)
    );
    // Pulizia memoria (gestita dal garbage collector in TypeScript)
    return result;
  }

  /**
   * Decrittografa la chiave principale utilizzando un nome utente, una passphrase e i dati crittografati.
   * @param username - Il nome utente dell'utente.
   * @param passphrase - La passphrase dell'utente.
   * @param encryptedb64Data - I dati crittografati da decrittografare in base 64.
   * @returns La chiave principale decrittografata.
   */
  public async decryptMasterKey(
    username: string,
    passphrase: string,
    encryptedb64Data: string
  ): Promise<ArrayBuffer> {
    //debugger
    const encryptedData = CryptoUtilities.base64ToArrayBuffer(encryptedb64Data);
    const mac = encryptedData.slice(0, 32); // Assumendo output SHA256
    const encMK = encryptedData.slice(32);
    const salt = await this.cryptoService.hashSHA256(username);
    //const encKey = await this.deriveKey(passphrase, salt) // Modificare per usare PBKDF2
    const encKey = await this.cryptoService.pbkdf2(passphrase, salt); // Aggiungere questa linea se si implementa PBKDF2
    const macKey = await this.deriveMacKey(username, passphrase);
    const calculatedMac = await this.cryptoService.calculateMac(macKey, encMK);

    if (!CryptoUtilities.arrayBufferEquals(calculatedMac, mac)) {
      /*console.error(
        '######## ERRORE: il mac calcolato e quello inviato NON coincidono!! ########'
      )*/
      throw new Error('MAC verification failed'); // Commentare questa linea se si desidera solo loggare
    }

    const decMK = await this.cryptoService.decryptCBC(encKey, encMK);

    return decMK;
  }

  /**
   * Crittografa una catena di chiavi utilizzando una chiave principale.
   * @param masterKey - La chiave principale da utilizzare per la crittografia.
   * @param keyChain - L'oggetto della catena di chiavi da crittografare.
   * @returns I dati crittografati della catena di chiavi.
   */
  public async encryptKeyChain(
    masterKey: ArrayBuffer,
    keyChain: object
  ): Promise<string> {
    const iv = await this.cryptoService.generateIV();
    const data = JSON.stringify(keyChain);

    const encryptedData = await this.cryptoService.encryptCBC(
      masterKey,
      CryptoUtilities.UTF8StringToArrayBuffer(data),
      iv
    );
    return CryptoUtilities.arrayBufferToBase64(encryptedData);
  }

  /**
   * Decrittografa una catena di chiavi utilizzando una chiave principale.
   * @param masterKey - La chiave principale da utilizzare per la decrittografia.
   * @param encryptedData - I dati crittografati da decrittografare.
   * @returns L'oggetto della catena di chiavi decrittografata.
   */
  public async decryptKeyChain(
    masterKey: ArrayBuffer,
    b64EncryptedData: string
  ): Promise<{ EZ_personal: string }> {
    const encryptedData = CryptoUtilities.base64ToArrayBuffer(b64EncryptedData);

    const decrypted = await this.cryptoService.decryptCBC(
      masterKey,
      encryptedData
    );
    return JSON.parse(CryptoUtilities.arrayBufferToUTF8String(decrypted));
  }

  /**
   * Funzione per la generazione di una nuova DEK
   *
   * @param ezKey
   * @param dek
   * @returns l'IV casuale generato in fase di encrypting concatenato all'DEK cifrata (ENC_DEK)
   */
  public async encryptDek(
    ezKey: ArrayBuffer,
    dek: ArrayBuffer
  ): Promise<ArrayBuffer> {
    const iv = await this.cryptoService.generateIV();

    return await this.cryptoService.encryptCBC(ezKey, dek, iv); // ritorno un array di bit
  }

  /**
   * Funzione per ricavare la DEK da ENC_DEK
   *
   * @param ezKey
   * @param encryptedDEK
   * @returns il DEK in chiaro
   */
  public async decryptDek(ezKey: ArrayBuffer, encryptedDEK: ArrayBuffer) {
    return await this.cryptoService.decryptCBC(ezKey, encryptedDEK);
  }

  /**
   * Deriva una chiave MAC utilizzando un nome utente e una passphrase.
   * @param username - Il nome utente dell'utente.
   * @param passphrase - La passphrase dell'utente.
   * @returns La chiave MAC derivata.
   */
  private async deriveMacKey(
    username: string,
    passphrase: string
  ): Promise<ArrayBuffer> {
    const hashedPassphrase = await this.cryptoService.hashSHA256(
      CryptoUtilities.UTF8StringToArrayBuffer(passphrase)
    );
    return this.cryptoService.hashSHA256(
      CryptoUtilities.concatArrayBuffer(
        CryptoUtilities.UTF8StringToArrayBuffer(username),
        hashedPassphrase
      )
    );
  }
}

export default EncryptionService;
